import { ApiHandler } from '../api-handler';
import { ApiEndpoints, HttpMethod } from '../constants';
import { GeneralApiResponse } from '../types';
import { buildQueryParams } from '../utils';
import {
  GetOfferingsData,
  PatientPinSettings,
  UpdatePatientPinPayload,
  BillingSetupApiResponse,
  BillingSummaryApiResponse,
  BillingAutoConversionApiResponse,
  ProfileApiResponse,
  ProfileUpdatePayload,
  ResetPasswordPayload,
  SignUpReqBody,
  ClinicianRegistrationPayload,
  ContactRequestPayload,
  UsernameSuggestionsApiResponse,
} from './types';

export const UserApiService = {
  signUp: (data: SignUpReqBody) =>
    ApiHandler.makeApiRequest<GeneralApiResponse, SignUpReqBody>({
      endpoint: ApiEndpoints.Users.Base,
      method: HttpMethod.Post,
      withAuth: false,
      data,
    }),

  sendRegistrationVerificationEmail: () =>
    ApiHandler.makeApiRequest<GeneralApiResponse>({
      endpoint: ApiEndpoints.Users.RegistrationVerificationEmail,
      method: HttpMethod.Post,
      rejectOnError: true,
    }),

  registerClinician: (data: ClinicianRegistrationPayload) =>
    ApiHandler.makeApiRequest<
      GeneralApiResponse<{
        registrationStatus: 'complete';
        clinicId?: string;
        userId?: string;
        doctorId?: string;
      }>,
      ClinicianRegistrationPayload
    >({
      endpoint: ApiEndpoints.Users.ClinicianRegistration,
      method: HttpMethod.Post,
      data,
      rejectOnError: true,
    }),

  sendContactRequest: (data: ContactRequestPayload) =>
    ApiHandler.makeApiRequest<GeneralApiResponse, ContactRequestPayload>({
      endpoint: ApiEndpoints.Users.ContactRequest,
      method: HttpMethod.Post,
      data,
      rejectOnError: true,
    }),

  resendVerificationEmail: (email: string) =>
    ApiHandler.makeApiRequest<void, { email: string }>({
      endpoint: ApiEndpoints.Users.ResendVerification,
      method: HttpMethod.Post,
      withAuth: false,
      data: { email },
    }),

  // Called after Firebase sign-in to link Firebase UID and retrieve profile
  syncUser: () =>
    ApiHandler.makeApiRequest<ProfileApiResponse>({
      endpoint: ApiEndpoints.Users.Sync,
      method: HttpMethod.Get,
    }),

  getProfile: () =>
    ApiHandler.makeApiRequest<ProfileApiResponse>({
      endpoint: ApiEndpoints.Users.Profile,
      method: HttpMethod.Get,
    }),

  updateProfile: (payload: ProfileUpdatePayload, rejectOnError = false) =>
    ApiHandler.makeApiRequest<GeneralApiResponse, ProfileUpdatePayload>({
      endpoint: ApiEndpoints.Users.Profile,
      method: HttpMethod.Put,
      data: payload,
      rejectOnError,
    }),

  getUsernameSuggestions: (base: string, limit = 5) => {
    const query = buildQueryParams({ name: base, limit });
    return ApiHandler.makeApiRequest<UsernameSuggestionsApiResponse>({
      endpoint: ApiEndpoints.Users.UsernameSuggestions,
      method: HttpMethod.Get,
      withAuth: false,
      query,
      rejectOnError: true,
    });
  },

  forgetPassword: (email: string) =>
    ApiHandler.makeApiRequest<GeneralApiResponse, { email: string }>({
      endpoint: ApiEndpoints.Users.ForgotPassword,
      method: HttpMethod.Post,
      withAuth: false,
      data: { email },
    }),

  verifyToken: (token: string) =>
    ApiHandler.makeApiRequest<GeneralApiResponse, { token: string }>({
      endpoint: ApiEndpoints.Users.VerifyResetToken,
      method: HttpMethod.Post,
      withAuth: false,
      data: { token },
    }),

  resetPassword: (payload: ResetPasswordPayload) =>
    ApiHandler.makeApiRequest<GeneralApiResponse, ResetPasswordPayload>({
      endpoint: ApiEndpoints.Users.ResetPassword,
      method: HttpMethod.Post,
      withAuth: false,
      data: payload,
    }),

  changePassword: (newPassword: string) =>
    ApiHandler.makeApiRequest<GeneralApiResponse, { newPassword: string }>({
      endpoint: ApiEndpoints.Users.ChangePassword,
      method: HttpMethod.Put,
      data: { newPassword },
    }),

  // Doctor Offerings picker (ADR 0001)
  getOfferings: () =>
    ApiHandler.makeApiRequest<GeneralApiResponse<GetOfferingsData>>({
      endpoint: ApiEndpoints.Users.Offerings,
      method: HttpMethod.Get,
    }),

  updateOfferings: (offerings: string[]) =>
    ApiHandler.makeApiRequest<
      GeneralApiResponse<{ enabled: string[] }>,
      { offerings: string[] }
    >({
      endpoint: ApiEndpoints.Users.Offerings,
      method: HttpMethod.Put,
      data: { offerings },
      rejectOnError: true,
    }),

  // Patient PIN settings (the signed-in doctor's own)
  getPatientPin: () =>
    ApiHandler.makeApiRequest<GeneralApiResponse<PatientPinSettings>>({
      endpoint: ApiEndpoints.Users.PatientPin,
      method: HttpMethod.Get,
      rejectOnError: true,
    }),

  updatePatientPin: (payload: UpdatePatientPinPayload) =>
    ApiHandler.makeApiRequest<
      GeneralApiResponse<PatientPinSettings>,
      UpdatePatientPinPayload
    >({
      endpoint: ApiEndpoints.Users.PatientPin,
      method: HttpMethod.Put,
      data: payload,
      rejectOnError: true,
    }),

  setUpBilling: () =>
    ApiHandler.makeApiRequest<BillingSetupApiResponse>({
      endpoint: ApiEndpoints.Users.BillingSetup,
      method: HttpMethod.Post,
      rejectOnError: true,
    }),

  getBillingSummary: () =>
    ApiHandler.makeApiRequest<BillingSummaryApiResponse>({
      endpoint: ApiEndpoints.Users.BillingSummary,
      method: HttpMethod.Get,
      rejectOnError: true,
    }),

  updateBillingAutoConversion: (enabled: boolean) =>
    ApiHandler.makeApiRequest<
      BillingAutoConversionApiResponse,
      { enabled: boolean }
    >({
      endpoint: ApiEndpoints.Users.BillingAutoConversion,
      method: HttpMethod.Put,
      data: { enabled },
      rejectOnError: true,
    }),
} as const;
